import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { PartsHierarchyMenu } from "./PartsHierarchyMenu";
import { Separator } from "@/components/ui/separator";
import { getNavIcon } from "@/lib/navigation-icons";
import { useMainNavigation, type NavigationGroup } from "@/hooks/useMainNavigation";
import { Skeleton } from "@/components/ui/skeleton";

export function MobileNavigation() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { apiGroups, mockItems, useMock, isLoading } = useMainNavigation();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden min-w-[44px] min-h-[44px]">
          <Menu className="h-5 w-5" />
          <span className="sr-only">Open menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[280px] p-0 overflow-y-auto">
        <SheetTitle className="px-4 py-4 border-b border-border text-lg font-semibold">
          Navigation
        </SheetTitle>
        <nav className="py-2">
          {isLoading ? (
            <div className="space-y-2 px-4 py-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full rounded-md" />
              ))}
            </div>
          ) : !useMock && apiGroups ? (
            // ─── API-driven ───
            apiGroups.map((group) =>
              group.type === "link" ? (
                <Link
                  key={group.title}
                  to={group.url || "#"}
                  onClick={() => setOpen(false)}
                  className={cn(
                    "flex items-center px-4 py-3 text-sm font-medium transition-colors min-h-[44px]",
                    location.pathname === group.url
                      ? "text-primary bg-primary/5"
                      : "text-foreground hover:bg-muted"
                  )}
                >
                  {group.title}
                </Link>
              ) : (
                <ApiMobileNavGroup
                  key={group.title}
                  group={group}
                  location={location}
                  onNavigate={() => setOpen(false)}
                />
              )
            )
          ) : (
            // ─── Mock / fallback ───
            mockItems.map((item) =>
              item.type === "link" ? (
                <Link
                  key={item.name}
                  to={item.path!}
                  onClick={() => setOpen(false)}
                  className={cn(
                    "flex items-center px-4 py-3 text-sm font-medium transition-colors min-h-[44px]",
                    location.pathname === item.path
                      ? "text-primary bg-primary/5"
                      : "text-foreground hover:bg-muted"
                  )}
                >
                  {item.name}
                </Link>
              ) : (
                <MockMobileNavGroup
                  key={item.name}
                  group={item}
                  location={location}
                  onNavigate={() => setOpen(false)}
                  
                />
              )
            )
          )}
        </nav>
      </SheetContent>
    </Sheet>
  );
}

// ─── API-driven mobile group ───

function ApiMobileNavGroup({
  group,
  location,
  onNavigate,
}: {
  group: NavigationGroup;
  location: ReturnType<typeof useLocation>;
  onNavigate: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-3 text-sm font-medium text-foreground hover:bg-muted min-h-[44px]">
        {group.title}
        <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
      </CollapsibleTrigger>
      <CollapsibleContent>
        {group.children.some(c => c.title === "Shop RV Parts") && (
          <>
            <div className="px-2">
              <PartsHierarchyMenu onClose={onNavigate} />
            </div>
            <Separator className="my-1" />
          </>
        )}
        {group.children.filter(c => c.title !== "Shop RV Parts").map((child) => {
          const Icon = child.icon;
          return (
            <Link
              key={child.title}
              to={child.url}
              onClick={onNavigate}
              className={cn(
                "flex items-center gap-3 px-6 py-3 text-sm transition-colors min-h-[44px]",
                location.pathname === child.url
                  ? "text-primary bg-primary/5 font-medium"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
            >
              <Icon className="h-4 w-4" />
              {child.title}
            </Link>
          );
        })}
      </CollapsibleContent>
    </Collapsible>
  );
}

// ─── Mock mobile group (existing behaviour) ───

function MockMobileNavGroup({
  group,
  location,
  onNavigate,
}: {
  group: any;
  location: ReturnType<typeof useLocation>;
  onNavigate: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-3 text-sm font-medium text-foreground hover:bg-muted min-h-[44px]">
        {group.name}
        <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
      </CollapsibleTrigger>
      <CollapsibleContent>
        {group.isPartsMenu && (
          <>
            <div className="px-2">
              <PartsHierarchyMenu onClose={onNavigate} />
            </div>
            <Separator className="my-1" />
          </>
        )}
        {group.items
          ?.map((item: any) => {
            const Icon = getNavIcon(item.tag || '');
            return (
              <Link
                key={item.name}
                to={item.path}
                onClick={onNavigate}
                className={cn(
                  "flex items-center gap-3 px-6 py-3 text-sm transition-colors min-h-[44px]",
                  location.pathname === item.path
                    ? "text-primary bg-primary/5 font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )}
              >
                <Icon className="h-4 w-4" />
                {item.name}
              </Link>
            );
          })}
      </CollapsibleContent>
    </Collapsible>
  );
}
